import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  ChevronRight,
  Globe,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Search,
} from 'lucide-react';
import { fetchBureauSponsors, type SponsorListe } from '../services/api';
import { euros } from '../utils/format';
import { useAuth } from '../hooks/useAuth';
import SessionPerdue from '../components/SessionPerdue';

const statutLabels: Record<string, string> = {
  prospect: 'Prospect',
  actif: 'Actif',
  inactif: 'Inactif',
  refuse: 'Refusé',
};

const statutColors: Record<string, string> = {
  prospect: 'bg-amber-50 text-amber-700',
  actif: 'bg-green-50 text-green-700',
  inactif: 'bg-gray-100 text-gray-600 dark:text-gray-400 dark:bg-gray-700',
  refuse: 'bg-red-50 text-red-700',
};

/**
 * Annuaire des sponsors, affiché à la place du formulaire « Devenir sponsor »
 * pour les membres du bureau : eux n'ont pas à souscrire, ils ont à suivre.
 *
 * Deux montants par exercice, qui ne disent pas la même chose : l'encaissé —
 * ce qui est réellement rentré — et le souscrit, ce qui a été promis. Un écart
 * entre les deux, c'est une facture qui n'est pas payée.
 */
export default function SponsorListPage({ enOnglet = false }: { enOnglet?: boolean } = {}) {
  const [recherche, setRecherche] = useState('');

  const { user, isLoading: authLoading } = useAuth();
  const estBureau =
    user?.roles?.includes('ROLE_BUREAU') === true || user?.roles?.includes('ROLE_ADMIN') === true;

  const { data, isLoading, error } = useQuery({
    queryKey: ['bureau-sponsors'],
    queryFn: fetchBureauSponsors,
    // Sans ce garde-fou, un retour sur l'onglet après expiration de la session
    // relançait la requête et affichait « Erreur 401 » en pleine page.
    enabled: estBureau,
  });

  const sponsors = useMemo(() => data?.sponsors ?? [], [data?.sponsors]);
  const anneeN = data?.anneeN ?? new Date().getFullYear();
  const anneeN1 = data?.anneeN1 ?? anneeN - 1;

  const filtres = useMemo(() => {
    const terme = recherche.trim().toLowerCase();
    if (terme === '') return sponsors;

    return sponsors.filter((s) =>
      [s.nom, s.contact, s.email, s.telephone, s.siret, s.activite]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(terme)),
    );
  }, [sponsors, recherche]);

  const totaux = useMemo(
    () =>
      filtres.reduce(
        (acc, s) => ({
          montantN: acc.montantN + s.montantN,
          montantN1: acc.montantN1 + s.montantN1,
          souscritN: acc.souscritN + s.souscritN,
        }),
        { montantN: 0, montantN1: 0, souscritN: 0 },
      ),
    [filtres],
  );

  if (authLoading || (estBureau && isLoading)) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  // Une session expirée est le cas courant sur téléphone, où l'application
  // reste ouverte des jours. « Erreur 401 » n'apprend rien à personne : ce
  // qu'il faut, c'est le chemin pour se reconnecter.
  if (!estBureau || error) {
    return <SessionPerdue message={(error as Error | null)?.message} />;
  }

  return (
    // En onglet de la page Sponsors, la liste laisse son grand titre et ses
    // marges de page : le cadre est déjà posé au-dessus.
    <div className={enOnglet ? 'space-y-5' : 'p-4 lg:p-0 space-y-5 pb-24 lg:pb-0'}>
      <header className="space-y-1">
        {!enOnglet && (
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
            Nos sponsors
          </h1>
        )}
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {sponsors.length} sponsor{sponsors.length > 1 ? 's' : ''} — encaissé {anneeN} :{' '}
          <span className="font-semibold text-gray-700 dark:text-gray-200">
            {euros.format(totaux.montantN)}
          </span>
          {totaux.souscritN > totaux.montantN && (
            <> · reste à encaisser {euros.format(totaux.souscritN - totaux.montantN)}</>
          )}
        </p>
      </header>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          className="input pl-9"
          type="search"
          placeholder="Rechercher un sponsor, un contact, un SIRET…"
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
        />
      </div>

      {filtres.length === 0 ? (
        <div className="card text-center py-10 space-y-2">
          <Building2 className="w-10 h-10 mx-auto text-gray-300" />
          <p className="text-gray-500 dark:text-gray-400">
            {recherche ? 'Aucun sponsor ne correspond à cette recherche.' : 'Aucun sponsor enregistré.'}
          </p>
        </div>
      ) : (
        <>
          {/* Tableau — à partir de lg, où la largeur permet toutes les colonnes. */}
          <div className="hidden lg:block card p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-left text-gray-600 dark:text-gray-300">
                <tr>
                  <th className="px-4 py-3 font-medium">Sponsor</th>
                  <th className="px-4 py-3 font-medium">Coordonnées</th>
                  <th className="px-4 py-3 font-medium">Choix</th>
                  <th className="px-4 py-3 font-medium text-right">{anneeN}</th>
                  <th className="px-4 py-3 font-medium text-right">{anneeN1}</th>
                  <th className="px-4 py-3 w-8" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {filtres.map((s) => (
                  <tr
                    key={s.id}
                    className="hover:bg-primary-50/40 dark:hover:bg-gray-800/60 transition-colors"
                  >
                    <td className="px-4 py-3 align-top">
                      <Link to={`/sponsors/${s.id}`} className="font-medium text-gray-900 dark:text-gray-100 hover:text-primary-700">
                        {s.nom || 'Sans nom'}
                      </Link>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        {s.statut && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${statutColors[s.statut] ?? 'bg-gray-100 text-gray-600 dark:text-gray-400 dark:bg-gray-700'}`}>
                            {statutLabels[s.statut] ?? s.statut}
                          </span>
                        )}
                        {s.activite && (
                          <span className="text-xs text-gray-400 truncate max-w-[14rem]">{s.activite}</span>
                        )}
                      </div>
                    </td>

                    <td className="px-4 py-3 align-top text-gray-600 dark:text-gray-300 space-y-0.5">
                      {s.contact && <div className="font-medium text-gray-800 dark:text-gray-200">{s.contact}</div>}
                      {s.email && (
                        <div className="flex items-center gap-1.5 text-xs">
                          <Mail className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                          <a href={`mailto:${s.email}`} className="hover:text-primary-600 truncate">{s.email}</a>
                        </div>
                      )}
                      {s.telephone && (
                        <div className="flex items-center gap-1.5 text-xs">
                          <Phone className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                          <a href={`tel:${s.telephone}`} className="hover:text-primary-600">{s.telephone}</a>
                        </div>
                      )}
                      {s.adresse && (
                        <div className="flex items-start gap-1.5 text-xs">
                          <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-0.5" />
                          <span className="line-clamp-2">{s.adresse}</span>
                        </div>
                      )}
                      {s.siteWeb && (
                        <div className="flex items-center gap-1.5 text-xs">
                          <Globe className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                          <a href={s.siteWeb} target="_blank" rel="noreferrer" className="hover:text-primary-600 truncate">
                            {s.siteWeb.replace(/^https?:\/\//, '')}
                          </a>
                        </div>
                      )}
                    </td>

                    <td className="px-4 py-3 align-top">
                      <ListeChoix choix={s.choix} />
                    </td>

                    <td className="px-4 py-3 align-top text-right">
                      <MontantAnnee encaisse={s.montantN} souscrit={s.souscritN} />
                    </td>
                    <td className="px-4 py-3 align-top text-right">
                      <MontantAnnee encaisse={s.montantN1} souscrit={s.souscritN1} />
                    </td>

                    <td className="px-4 py-3 align-top text-right">
                      <Link
                        to={`/sponsors/${s.id}`}
                        title="Ouvrir la fiche"
                        className="inline-flex text-gray-400 hover:text-primary-600"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 dark:bg-gray-800 font-medium text-gray-700 dark:text-gray-200">
                <tr>
                  <td className="px-4 py-3" colSpan={3}>Total</td>
                  <td className="px-4 py-3 text-right">{euros.format(totaux.montantN)}</td>
                  <td className="px-4 py-3 text-right">{euros.format(totaux.montantN1)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Cartes — sous lg, le tableau ne tiendrait pas sans défilement latéral. */}
          <ul className="lg:hidden space-y-3">
            {filtres.map((s) => (
              <li key={s.id}>
                <Link to={`/sponsors/${s.id}`} className="card block space-y-2 active:bg-gray-50">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{s.nom || 'Sans nom'}</p>
                      {s.contact && <p className="text-sm text-gray-500 truncate dark:text-gray-400">{s.contact}</p>}
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-300 shrink-0" />
                  </div>

                  <ListeChoix choix={s.choix} />

                  <div className="flex items-center justify-between text-sm pt-1 border-t border-gray-100">
                    <span className="text-gray-500 dark:text-gray-400">{anneeN}</span>
                    <MontantAnnee encaisse={s.montantN} souscrit={s.souscritN} />
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">{anneeN1}</span>
                    <MontantAnnee encaisse={s.montantN1} souscrit={s.souscritN1} />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function ListeChoix({ choix }: { choix: SponsorListe['choix'] }) {
  if (choix.length === 0) {
    return <span className="text-xs text-gray-400">—</span>;
  }

  return (
    <ul className="flex flex-wrap gap-1">
      {choix.map((c, i) => (
        <li
          key={`${c.support}-${i}`}
          className="text-xs px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
          title={c.montant > 0 ? `${c.support} — ${euros.format(c.montant)}` : c.support}
        >
          {c.support} · {c.libelle}
        </li>
      ))}
    </ul>
  );
}

/**
 * Encaissé en valeur principale ; le souscrit n'apparaît que s'il diffère,
 * c'est-à-dire quand il reste quelque chose à recouvrer.
 */
function MontantAnnee({ encaisse, souscrit }: { encaisse: number; souscrit: number }) {
  if (encaisse === 0 && souscrit === 0) {
    return <span className="text-gray-300">—</span>;
  }

  return (
    <div>
      <div className="font-semibold text-gray-900 dark:text-gray-100">{euros.format(encaisse)}</div>
      {souscrit > encaisse && (
        <div className="text-xs text-amber-600" title="Montant souscrit non encore encaissé">
          reste {euros.format(souscrit - encaisse)}
        </div>
      )}
    </div>
  );
}
